/**
 * BATCH TRANSFERS HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • generateBatchTransferPreview()  - POST /wallets/batch-transfer/generate - Parse CSV/XLSX file and return preview
 * • processBatchTransfers()         - POST /wallets/batch-transfer/process - Process batch transfers asynchronously
 * • getFailedBatchTransfers()       - GET /wallets/batch-transfer/failed - Get paginated list of failed batch transfers
 * • getBatchTransferQueuedStatus()  - GET /wallets/batch-transfer/queued-status?from_entity&wallet_type - Whether a wallet type has queued payments
 * • retryFailedBatchTransfer()      - POST /wallets/batch-transfer/retry/{id} - Retry a failed batch transfer
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useGenerateBatchTransferPreview() - Mutation: Upload file and generate batch transfer preview
 * • useProcessBatchTransfers()        - Mutation: Process batch transfers to multiple recipients
 * • useFailedBatchTransfers()         - Query: Fetch paginated list of failed batch transfers
 * • useBatchTransferQueuedStatus()    - Query: Whether a wallet type has queued payments
 * • useRetryFailedBatchTransfer()     - Mutation: Retry a specific failed batch transfer
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • File upload support (CSV/XLSX) via multipart/form-data
 * • Only users with employer role can use these endpoints
 * • Batch limit: up to 2000 transactions per batch
 * • Rate limiting: 10 requests per minute per user for processing/retry
 * • Async processing: batch transfers processed in background
 * • Automatic query invalidation on batch operations
 * • Currency conversion support with validation
 * • Automatic withdrawal option for transfers
 * • Failed transfer tracking with retry capability
 * • Fail types: full_batch, group, transaction, system
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import fetchAPI from '@/lib/fetch-api';

// ==================== INTERFACES ====================

interface Response {
  message: string;
  status: number;
  type: string;
}

// Currency information in batch transfer
interface BatchTransferCurrency {
  id: number;
  name?: string;
  code: string;
  flag_code?: string;
  symbol?: string;
  billing_currency?: boolean;
}

// User information in batch transfer
interface BatchTransferUser {
  id: number;
  email: string;
  name: string;
}

// Validation status for each field in a batch transfer row
interface BatchTransferValidation {
  email: boolean;
  from_wallet: boolean;
  to_wallet: boolean;
  amount: boolean;
  transfer_description: boolean;
  autowithdrawal: boolean;
  bank_account_name: boolean;
}

// Single row in batch transfer preview
export interface BatchTransferPreviewRow {
  email: string;
  from_wallet: string;
  to_wallet: string;
  amount: number;
  transfer_description: string;
  autowithdrawal: 'Yes' | 'No' | string;
  bank_account_name: string | null;
  transfer_bank: boolean;
  transfer_bank_id: number | null;
  user: BatchTransferUser | null;
  not_invited: number;
  from_currency: BatchTransferCurrency;
  to_currency: BatchTransferCurrency;
  converted: number;
  converted_amount: number;
  valid: BatchTransferValidation;
}

// Single transfer item for process endpoint
export interface BatchTransferProcessItem {
  user: {
    id: number;
    email: string;
    name: string;
  };
  from_currency: {
    id: number;
    code: string;
  };
  to_currency: {
    id: number;
    code: string;
  };
  amount: number;
  converted_amount: number;
  converted: number;
  transfer_description: string;
  autowithdrawal: 'Yes' | 'No' | string;
  bank_account_name?: string | null;
  transfer_bank?: boolean;
  transfer_bank_id?: number | null;
}

// Request body for process endpoint
interface ProcessBatchTransfersRequest {
  data: BatchTransferProcessItem[];
}

// Response from process endpoint
interface ProcessBatchTransfersResponse extends Response {
  data: unknown[];
}

// Fail types for failed batch transfers
export type FailedBatchTransferFailType = 'full_batch' | 'group' | 'transaction' | 'system';

// Single transfer item within a failed batch
export interface FailedBatchTransferItem {
  user: {
    id: number;
    email: string;
    name: string;
  };
  amount: number;
  converted: number;
  converted_amount: number;
  from_currency: {
    id: number;
    code: string;
  };
  to_currency: {
    id: number;
    code: string;
  };
  transfer_description: string;
  autowithdrawal: string;
  transfer_bank: boolean;
  transfer_bank_id: number | null;
  bank_account_name: string | null;
}

// Single failed batch transfer item
export interface FailedBatchTransfer {
  id: number;
  user_id: number;
  organisation_id: number;
  fail_type: FailedBatchTransferFailType;
  reason: string;
  data: FailedBatchTransferItem[];
  created_at: string;
  updated_at: string;
}

// Pagination info for failed batch transfers
interface FailedBatchTransferPagination {
  currentPage: number;
  perPage: number;
  total: number;
  lastPage: number;
  nextPageUrl: string | null;
}

// Response from failed batch transfers endpoint
interface FailedBatchTransfersResponse {
  success: boolean;
  message: string;
  data: FailedBatchTransfer[];
  pagination: FailedBatchTransferPagination;
}

// Query parameters for failed batch transfers endpoint
export interface FailedBatchTransfersParams {
  page?: number;
  per_page?: number;
  fail_type?: FailedBatchTransferFailType;
  user_id?: number;
  from_entity?: boolean;
}

export interface BatchTransferQueuedStatus {
  has_queued_payments: boolean;
}

export interface BatchTransferQueuedStatusParams {
  from_entity: boolean;
}

type QueuedStatusWalletType = 'entity' | 'default';

// Response from retry endpoint
interface RetryFailedBatchTransferResponse extends Response {
  data: unknown[];
}

// ==================== API FUNCTIONS ====================

/**
 * Generates a batch transfer preview from an uploaded CSV/XLSX file
 * Only users with the employer role can use this endpoint.
 * @param {File} file - The CSV or XLSX file containing batch transfers
 * @throws {Error} If no authentication token is found, user lacks employer role, or the API request fails
 * @returns {Promise<BatchTransferPreviewRow[]>} Array of batch transfer preview rows with validation
 */
const generateBatchTransferPreview = async (file: File): Promise<BatchTransferPreviewRow[]> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchAPI<BatchTransferPreviewRow[]>('/wallets/batch-transfer/generate', {
    method: 'POST',
    body: formData,
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(
      response.error || response.message || 'Failed to generate batch transfer preview'
    );
  }

  return response.data!;
};

/**
 * Processes a batch of wallet transfers from the authenticated user (employer) to multiple recipients
 * Only users with the employer role can use this endpoint.
 * Rate limited to 10 requests per minute per user.
 * Batch limit: up to 2000 transactions per batch.
 * Processing is asynchronous - endpoint returns immediately while transfers process in background.
 * @param {ProcessBatchTransfersRequest} requestData - The batch transfer request data
 * @throws {Error} If no authentication token is found, user lacks employer role, rate limit exceeded, or the API request fails
 * @returns {Promise<ProcessBatchTransfersResponse>} The process response (success message)
 */
const processBatchTransfers = async (
  requestData: ProcessBatchTransfersRequest
): Promise<ProcessBatchTransfersResponse> => {
  const response = await fetchAPI<ProcessBatchTransfersResponse>(
    '/wallets/batch-transfer/process',
    {
      method: 'POST',
      body: JSON.stringify(requestData),
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to process batch transfers');
  }

  return response.data!;
};

/**
 * Retrieves a paginated list of failed batch transfers
 * Only users with the employer role can use this endpoint.
 * Results are automatically filtered to show only failed batch transfers from the user's organisation.
 * @param {FailedBatchTransfersParams} params - Query parameters for pagination and filtering
 * @throws {Error} If no authentication token is found, user lacks employer role, or the API request fails
 * @returns {Promise<FailedBatchTransfersResponse>} Paginated list of failed batch transfers
 */
const getFailedBatchTransfers = async (
  params: FailedBatchTransfersParams = {}
): Promise<FailedBatchTransfersResponse> => {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) {
    searchParams.append('page', params.page.toString());
  }
  if (params.per_page !== undefined) {
    searchParams.append('per_page', params.per_page.toString());
  }
  if (params.fail_type !== undefined) {
    searchParams.append('fail_type', params.fail_type);
  }
  if (params.user_id !== undefined) {
    searchParams.append('user_id', params.user_id.toString());
  }
  if (params.from_entity !== undefined) {
    searchParams.append('from_entity', params.from_entity ? '1' : '0');
  }

  const queryString = searchParams.toString();
  const url = `/wallets/batch-transfer/failed${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<FailedBatchTransfer[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch failed batch transfers');
  }

  const transfers = response.data ?? [];

  return {
    success: response.success ?? true,
    message: response.message ?? '',
    data: transfers,
    pagination: response.pagination
      ? {
          currentPage: response.pagination.current_page,
          perPage: response.pagination.per_page,
          total: response.pagination.total,
          lastPage: response.pagination.last_page,
          nextPageUrl: response.pagination.next_page_url ?? null,
        }
      : {
          currentPage: 1,
          perPage: transfers.length,
          total: transfers.length,
          lastPage: 1,
          nextPageUrl: null,
        },
  };
};

/**
 * Reports whether the given wallet type has queued batch payments.
 * The endpoint requires both flags: entity is from_entity=1 and wallet_type=entity,
 * regular is from_entity=0 and wallet_type=default.
 * @param {BatchTransferQueuedStatusParams} params - Wallet type to check
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<BatchTransferQueuedStatus>} Queued flag for that wallet type
 */
const getBatchTransferQueuedStatus = async (
  params: BatchTransferQueuedStatusParams
): Promise<BatchTransferQueuedStatus> => {
  const walletType: QueuedStatusWalletType = params.from_entity ? 'entity' : 'default';
  const searchParams = new URLSearchParams();
  searchParams.append('from_entity', params.from_entity ? '1' : '0');
  searchParams.append('wallet_type', walletType);

  const response = await fetchAPI<BatchTransferQueuedStatus>(
    `/wallets/batch-transfer/queued-status?${searchParams.toString()}`,
    {
      method: 'GET',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(
      response.error || response.message || 'Failed to fetch batch transfer queued status'
    );
  }

  return {
    has_queued_payments: response.data?.has_queued_payments ?? false,
  };
};

/**
 * Failed batch rows for one wallet type, only when queued-status says that wallet is queued.
 * Used after a top-up so a regular wallet top-up does not retry entity queued payments.
 * @param {boolean} fromEntity - True for the business/entity wallet
 * @returns {Promise<FailedBatchTransfer[]>} Queued rows to retry, or an empty list
 */
const getQueuedFailedBatchTransfers = async (
  fromEntity: boolean
): Promise<FailedBatchTransfer[]> => {
  const status = await getBatchTransferQueuedStatus({ from_entity: fromEntity });
  if (!status.has_queued_payments) return [];

  const result = await getFailedBatchTransfers({ from_entity: fromEntity });
  return result.data;
};

/**
 * Retries a previously failed batch transfer
 * Only users with the employer role can use this endpoint.
 * Rate limited to 10 requests per minute per user.
 * Processing is asynchronous - endpoint returns immediately while retry processes in background.
 * @param {number} failedBatchId - The ID of the failed batch transfer to retry
 * @throws {Error} If no authentication token is found, user lacks employer role, rate limit exceeded, or the API request fails
 * @returns {Promise<RetryFailedBatchTransferResponse>} The retry response (success message)
 */
const retryFailedBatchTransfer = async (
  failedBatchId: number
): Promise<RetryFailedBatchTransferResponse> => {
  const response = await fetchAPI<RetryFailedBatchTransferResponse>(
    `/wallets/batch-transfer/retry/${failedBatchId}`,
    {
      method: 'POST',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to retry batch transfer');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to generate batch transfer preview from uploaded file
 * Only users with the employer role can use this endpoint.
 * Accepts CSV or XLSX files only.
 * @returns {UseMutationResult} Mutation result for generating batch transfer preview
 */
export function useGenerateBatchTransferPreview(): UseMutationResult<
  BatchTransferPreviewRow[],
  Error,
  File
> {
  return useMutation({
    mutationFn: generateBatchTransferPreview,
  });
}

/**
 * Hook to process batch transfers to multiple recipients
 * Only users with the employer role can use this endpoint.
 * Rate limited to 10 requests per minute per user.
 * Batch limit: up to 2000 transactions per batch.
 * Processing is asynchronous.
 * @returns {UseMutationResult} Mutation result for processing batch transfers
 */
export function useProcessBatchTransfers(): UseMutationResult<
  ProcessBatchTransfersResponse,
  Error,
  ProcessBatchTransfersRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processBatchTransfers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to fetch paginated list of failed batch transfers
 * Only users with the employer role can use this endpoint.
 * Results are automatically filtered to show only failed batch transfers from the user's organisation.
 * @param {FailedBatchTransfersParams} params - Query parameters for pagination and filtering
 * @param {boolean} enabled - Whether the query should be enabled (default: true)
 * @returns {UseQueryResult} Query result with failed batch transfers data and pagination
 */
export function useFailedBatchTransfers(
  params: FailedBatchTransfersParams = {},
  enabled: boolean = true
): UseQueryResult<FailedBatchTransfersResponse, Error> {
  return useQuery({
    queryKey: ['failed-batch-transfers', params],
    queryFn: () => getFailedBatchTransfers(params),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to check whether a wallet type has queued batch payments.
 * from_entity true is the business/entity wallet; false is the regular wallet.
 * @param {BatchTransferQueuedStatusParams} params - Wallet type to check
 * @param {boolean} enabled - Whether the query should be enabled (default: true)
 * @returns {UseQueryResult} Query result with the queued flag
 */
export function useBatchTransferQueuedStatus(
  params: BatchTransferQueuedStatusParams,
  enabled: boolean = true
): UseQueryResult<BatchTransferQueuedStatus, Error> {
  return useQuery({
    queryKey: ['batch-transfer-queued-status', params],
    queryFn: () => getBatchTransferQueuedStatus(params),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to retry a specific failed batch transfer
 * Only users with the employer role can use this endpoint.
 * Rate limited to 10 requests per minute per user.
 * Processing is asynchronous.
 * @returns {UseMutationResult} Mutation result for retrying failed batch transfer
 */
export function useRetryFailedBatchTransfer(): UseMutationResult<
  RetryFailedBatchTransferResponse,
  Error,
  number
> {
  return useMutation({
    mutationFn: retryFailedBatchTransfer,
  });
}

/**
 * Directly fetch the current failed batch transfers list.
 * Used for polling after a retry to check if the transfer resolved.
 */
export { getBatchTransferQueuedStatus, getFailedBatchTransfers, getQueuedFailedBatchTransfers };
